import { useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MC_TREE_SELECT, collectEmbedded } from '@/lib/mcMembership';

export interface ContextTreeFeed {
  id: string;
  feedId: string;
  displayName: string | null;
  targetCountry: string | null;
  contentLanguage: string | null;
  lastSyncedAt: string | null;
  updatedAt: string | null;
  /** null = Zahl (noch) unbekannt — UI zeigt dann keine. 0 ist eine echte 0. */
  productCount: number | null;
  uploaded: boolean;
}

export interface ContextTreeMc {
  id: string;
  externalId: string;
  name: string | null;
  accessStatus: string;
  isMca: boolean;
  feeds: ContextTreeFeed[];
  hiddenAutomaticCount: number;
}

export interface ContextTreeAccount {
  id: string;
  email: string | null;
  displayName: string | null;
  merchantCenters: ContextTreeMc[];
}

/**
 * Rohe Zeilenform des einen verschachtelten Selects (`google_accounts` ->
 * `merchant_center_members` -> `merchant_centers` -> `feeds`). Der Select ist
 * ein zusammengesetzter Template-String, den der generierte Typ nicht
 * aufloesen kann -- deshalb ein schmaler lokaler Zeilentyp als Vertrag der
 * Abfrage (`types.ts` ist seit Migration B, Task 15, regeneriert und kennt
 * `merchant_center_members`).
 */
interface RawFeedRow {
  id: string;
  feed_id: string;
  display_name: string | null;
  feed_type: string | null;
  feed_input_type: string | null;
  target_country: string | null;
  content_language: string | null;
  last_synced_at: string | null;
  updated_at: string | null;
}

interface RawMcRow {
  id: string;
  merchant_center_id: string;
  name: string | null;
  parent_merchant_center_id: string | null;
  access_status: string | null;
  feeds: RawFeedRow[] | null;
}

/**
 * Eine Zeile aus `merchant_center_members`, so wie sie im Baum-Select
 * eingebettet ist: die Rolle plus die MC, fuer die sie den Grant traegt.
 * `merchant_centers` ist ein Zu-eins-Embed (FK `merchant_center_id`) und kommt
 * defensiv als Objekt ODER Array an (`firstEmbedded`); vor Migration B ist es
 * fuer eine nur ueber `role='member'` erreichbare MC `null` (die RLS-Kette von
 * `merchant_centers` kennt bis dahin nur den Eigentuemer, siehe MC_TREE_SELECT).
 */
interface RawMemberRow {
  role: string | null;
  merchant_centers: RawMcRow | RawMcRow[] | null;
}

interface RawAccountRow {
  id: string;
  email: string | null;
  display_name: string | null;
  merchant_center_members: RawMemberRow[] | null;
}

const TREE_KEY = ['google-accounts', 'context-tree'] as const;
const COUNTS_KEY = ['google-accounts', 'context-tree', 'counts'] as const;

const TREE_SELECT = `id, email, display_name, ${MC_TREE_SELECT}`;

function isNonSupplementalFeed(f: RawFeedRow): boolean {
  return f.feed_type !== 'supplemental';
}

function isAutomaticFeed(f: RawFeedRow): boolean {
  return f.feed_input_type === 'automatic';
}

/**
 * Flacht die Mitgliedschaftszeilen EINES Google-Kontos zu einer Liste seiner
 * Merchant Center — "MCs eines Google-Kontos" heisst seit der Mitgliedschafts-
 * Umstellung "MCs, fuer die dieses Konto der Grant einer Mitgliedschaft ist",
 * unabhaengig von `role` (owner und member erscheinen gleichberechtigt). Eine
 * Mitgliedschaftszeile ohne sichtbare Einbettung (vor Migration B fuer
 * `role='member'`) wird uebersprungen statt den Baum aufzuhalten. Dedupliziert
 * nach `merchant_centers.id` INNERHALB dieses Accounts (falls derselbe Nutzer
 * je MC theoretisch zwei Grants haette) — nicht kontouebergreifend, eine MC
 * darf unter zwei verschiedenen Google-Konten desselben Nutzers durchaus
 * zweimal im Baum auftauchen. Der Flatten/Dedupe-Kern liegt seit Fix-Runde 1
 * (Important 1) in `collectEmbedded` (`lib/mcMembership.ts`) -- dieselbe
 * Funktion, die auch `SelectFeedStep.tsx`, `SelectionContext.tsx`,
 * `GoogleAccounts.tsx` und `MerchantCenters.tsx` benutzen, statt ihn fuenffach
 * zu kopieren.
 */
function flattenMemberMcs(members: RawMemberRow[] | null | undefined): RawMcRow[] {
  return collectEmbedded(
    members ?? [],
    (member) => member.merchant_centers,
    (_member, mc) => mc.id,
  ).map(({ embed }) => embed);
}

/**
 * Nullable-String-Vergleich mit Nulls zuletzt — bildet
 * `.order(col, { ascending: true, nullsFirst: false })` nach, wie es
 * `ContextSelector.tsx` fuer die Feed-Sortierung nutzt (dort serverseitig
 * pro MC, hier client-seitig fuer den ganzen Baum).
 */
function compareNullableStrings(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
}

function compareFeeds(a: RawFeedRow, b: RawFeedRow): number {
  return (
    compareNullableStrings(a.target_country, b.target_country) ||
    compareNullableStrings(a.content_language, b.content_language) ||
    compareNullableStrings(a.display_name, b.display_name)
  );
}

/**
 * Reine Baum-Bau-Funktion, separat exportiert damit sie ohne React getestet
 * werden kann. Regeln (Task-Brief):
 * - `feed_type === 'supplemental'` raus (unsere eigenen GMC-Supplemental-Feeds,
 *   nie in der UI waehlbar).
 * - `feed_input_type === 'automatic'` raus, aber gezaehlt in
 *   `hiddenAutomaticCount` (Google-gecrawlte Feeds, ContextSelector.tsx
 *   blendet sie ebenso aus).
 * - Merchant Centers alphabetisch nach `name`.
 * - Feeds sortiert nach `target_country, content_language, display_name`
 *   (heutige Reihenfolge, ContextSelector.tsx ~Z.213-217).
 * - `isMca` = irgendeine ANDERE MC im ganzen Baum hat
 *   `parent_merchant_center_id === mc.id` (Kind-MC muss nicht im selben
 *   `google_accounts`-Eintrag stehen wie ihr Verwaltungskonto).
 * - `productCount` = `counts?.get(feed.id) ?? null` — `??`, nie `||`: 0 ist
 *   eine echte Produktzahl, nicht "unbekannt".
 * - Merchant Center eines Accounts kommen ueber `merchant_center_members`
 *   (Mitgliedschafts-Umstellung, Task 10) — `flattenMemberMcs` traegt Filter
 *   und Dedupe.
 */
export function buildTree(
  rawAccounts: RawAccountRow[] | null | undefined,
  counts: Map<string, number> | undefined,
  uploaded: Set<string> | undefined,
): ContextTreeAccount[] {
  const accounts = rawAccounts ?? [];
  const mcsByAccount = accounts.map((acc) => flattenMemberMcs(acc.merchant_center_members));

  const parentIds = new Set<string>();
  for (const mcs of mcsByAccount) {
    for (const mc of mcs) {
      if (mc.parent_merchant_center_id) parentIds.add(mc.parent_merchant_center_id);
    }
  }

  return accounts.map((acc, i) => ({
    id: acc.id,
    email: acc.email ?? null,
    displayName: acc.display_name ?? null,
    merchantCenters: [...mcsByAccount[i]]
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((mc) => {
        const nonSupplemental = (mc.feeds ?? []).filter(isNonSupplementalFeed);
        const visibleFeeds = nonSupplemental.filter((f) => !isAutomaticFeed(f));
        const hiddenAutomaticCount = nonSupplemental.length - visibleFeeds.length;

        return {
          id: mc.id,
          externalId: mc.merchant_center_id,
          name: mc.name ?? null,
          accessStatus: mc.access_status ?? 'active',
          isMca: parentIds.has(mc.id),
          hiddenAutomaticCount,
          feeds: [...visibleFeeds].sort(compareFeeds).map((f) => ({
            id: f.id,
            feedId: f.feed_id,
            displayName: f.display_name ?? null,
            targetCountry: f.target_country ?? null,
            contentLanguage: f.content_language ?? null,
            lastSyncedAt: f.last_synced_at ?? null,
            updatedAt: f.updated_at ?? null,
            productCount: counts?.get(f.id) ?? null,
            uploaded: uploaded?.has(f.id) ?? false,
          })),
        };
      }),
  }));
}

/**
 * Laedt den GANZEN Baum (alle Accounts -> MCs -> Feeds) mit genau EINEM
 * verschachtelten `from().select()` plus ZWEI RPC-Calls (`feed_product_counts`
 * fuer Stueckzahlen, `get_uploaded_feed_ids` fuer den Upload-Haken) statt
 * eines Fan-outs aus Einzel-Counts pro Feed (der alte `ContextSelector`-Pfad
 * fragte pro MC-Wechsel neu nach; hier ist alles in einem Rutsch da, lazy
 * hinter `enabled`, weil ein Popover ihn nicht bei jedem App-Start braucht).
 */
export function useContextTree(enabled: boolean): {
  accounts: ContextTreeAccount[];
  isLoading: boolean;
  isError: boolean;
  /** Neben-Abfragen (Produktzahlen, Upload-Haken) gescheitert, der Baum selbst steht. */
  detailsFailed: boolean;
} {
  const tree = useQuery({
    queryKey: TREE_KEY,
    enabled,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('google_accounts')
        .select(TREE_SELECT)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows: RawAccountRow[] = data ?? [];
      return rows;
    },
  });

  const counts = useQuery({
    queryKey: COUNTS_KEY,
    enabled,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('feed_product_counts');
      if (error) throw error;
      const map = new Map<string, number>();
      for (const row of data ?? []) map.set(row.feed_id, Number(row.product_count));
      return map;
    },
  });

  const allFeedIds = useMemo(() => {
    const ids: string[] = [];
    for (const acc of tree.data ?? []) {
      for (const mc of flattenMemberMcs(acc.merchant_center_members)) {
        for (const f of mc.feeds ?? []) {
          if (isNonSupplementalFeed(f) && !isAutomaticFeed(f)) ids.push(f.id);
        }
      }
    }
    return ids;
  }, [tree.data]);

  const uploaded = useQuery({
    queryKey: [...TREE_KEY, 'uploaded', allFeedIds.length],
    enabled: enabled && allFeedIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      // `get_uploaded_feed_ids` fehlt ebenfalls im generierten Funktions-Typ
      // (gleicher Rueckstand wie `access_status`, siehe Fixture-Kommentar
      // oben) — der fruehere Aufrufer `ContextSelector.tsx` rief sie schon
      // ungecastet auf, dieselbe Zeile wird hier fortgesetzt.
      const { data, error } = await supabase.rpc('get_uploaded_feed_ids', { p_feed_ids: allFeedIds });
      if (error) throw error;
      return new Set<string>((data ?? []) as string[]);
    },
  });

  useEffect(() => {
    if (!counts.isError) return;
    const e = (counts.error ?? {}) as { code?: unknown; message?: unknown };
    Sentry.captureException(new Error('context tree details failed'), {
      tags: { feature: 'context-switcher', action: 'tree-details' },
      extra: {
        query: 'feed_product_counts',
        code: typeof e.code === 'string' ? e.code : null,
        message: typeof e.message === 'string' ? e.message : null,
      },
    });
  }, [counts.isError, counts.error]);

  useEffect(() => {
    if (!uploaded.isError) return;
    const e = (uploaded.error ?? {}) as { code?: unknown; message?: unknown };
    Sentry.captureException(new Error('context tree details failed'), {
      tags: { feature: 'context-switcher', action: 'tree-details' },
      extra: {
        query: 'get_uploaded_feed_ids',
        code: typeof e.code === 'string' ? e.code : null,
        message: typeof e.message === 'string' ? e.message : null,
      },
    });
  }, [uploaded.isError, uploaded.error]);

  const accounts = useMemo(
    () => buildTree(tree.data, counts.data, uploaded.data),
    [tree.data, counts.data, uploaded.data],
  );

  return {
    accounts,
    isLoading: tree.isLoading,
    isError: tree.isError,
    detailsFailed: counts.isError || uploaded.isError,
  };
}
